/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 01/02/22
 */

import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import React, { forwardRef } from 'react';
import { Avatar, chakra, HStack, Text, VStack } from '@chakra-ui/react';
import Icons from '@/components/icons/Icons';

interface ProfileMenuButtonProps {
    userName: string;
    currentAssignment?: Assignment;
}

export const ProfileMenuButton = forwardRef<HTMLButtonElement, ProfileMenuButtonProps>((componentProps, ref) => {
    const { userName, currentAssignment, ...buttonProps } = componentProps;
    return (
        <chakra.button
            ref={ref}
            {...buttonProps}
            __css={{
                appearance: 'none',
                alignItems: 'center',
                outline: 0,
                '&:focus, &:hover': {
                    '.chakra-avatar': {
                        background: 'var(--chakra-colors-gray-200)',
                    },
                },
                '&:active .chakra-avatar': {
                    background: 'var(--chakra-colors-gray-300)',
                },
            }}
            _focus={{
                boxShadow: 'none',
                position: 'relative',
                outlineStyle: 'solid',
                outlineColor: 'white',
                outlineWidth: 3,
            }}
        >
            <HStack>
                <VStack spacing={0} alignItems={'flex-start'}>
                    <HStack spacing={0} flexWrap="nowrap">
                        <Text fontSize={'sm'} color={'white'} fontWeight={'bold'}>
                            {userName}
                        </Text>
                    </HStack>
                    {currentAssignment && (
                        <Text fontSize={'sm'} color={'white'} whiteSpace="nowrap">
                            {currentAssignment.assignmentNumber} - {currentAssignment.name}{' '}
                            {currentAssignment.inactive && '- Inactive'}
                        </Text>
                    )}
                </VStack>
                <Avatar icon={<Icons.ProfileIcon color={'black'} />} bg="var(--chakra-colors-gray-100)" />
            </HStack>
        </chakra.button>
    );
});

ProfileMenuButton.displayName = 'ProfileMenuButton';
